import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/Button";
import { Mark } from "@/components/brand/Logo";
import { ViewfinderFrame } from "@/components/brand/ViewfinderFrame";
import { useDocumentMeta } from "@/lib/useDocumentMeta";

/**
 * Marketing landing page at `/` (M12a).
 *
 * Public, anonymous-readable. Replaces the M11a `SeasonShell` index
 * with a brand-quality first-touch surface. Two CTAs split the
 * audiences:
 *
 *   1. "Tktic Hub" → /login?next=/hub — coaches/admins/families
 *      sign in and are routed to their default landing.
 *   2. "Matches & Livestreams" → /teams — anonymous viewers browse
 *      public teams without auth.
 *
 * Brand cover spec: lowercase typography, Display XL (88px) headline
 * with `tracking-hero`, Trust Navy + Electric Cyan token pair, the
 * viewfinder + O · X · cyan dotted-line vocabulary, no cyan on white.
 * The accent treatment for the "delivered." moment is rendered with
 * a cyan underline rule rather than cyan body text so it passes WCAG
 * AA in both light + dark themes (the brand book is explicit:
 * "never use cyan on white text").
 */
export function LandingPage() {
  useDocumentMeta({
    title: "tktic — coach feedback, delivered",
    description:
      "Game film, organized by team. Coach feedback that reaches the player. Live broadcasts so families never miss a match.",
    ogType: "website",
  });

  return (
    <main data-testid="landing-page" className="bg-canvas text-ink">
      <Hero />
      <FeatureBlocks />
      <AudienceCards />
      <FooterStrip />
    </main>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function Hero() {
  return (
    <section
      data-testid="landing-hero"
      className="mx-auto flex max-w-6xl flex-col gap-10 px-6 pt-12 pb-16 sm:pt-16 sm:pb-20 lg:flex-row lg:items-center lg:gap-16 lg:pt-24 lg:pb-28"
    >
      <div className="flex flex-1 flex-col gap-6">
        <h1
          data-testid="landing-hero-headline"
          className="font-display text-4xl font-bold leading-tight tracking-hero text-ink sm:text-5xl lg:text-[5.5rem]"
        >
          coach feedback,
          <br />
          <AccentLine>delivered.</AccentLine>
        </h1>
        <p
          data-testid="landing-hero-subhead"
          className="max-w-xl font-body text-lg leading-snug text-ink-secondary sm:text-xl"
        >
          tktic gives coaches, players, and families of youth football teams
          the language of a pro — raw match footage turned into focused
          feedback that reaches the player where they live.
        </p>
        <div
          data-testid="landing-hero-ctas"
          className="flex flex-col gap-3 sm:flex-row sm:items-center"
        >
          <Button asChild size="lg" variant="primary">
            <Link
              to="/login"
              search={{ next: "/hub" }}
              data-testid="landing-cta-hub"
            >
              tktic hub
              <span aria-hidden="true" className="ml-1 tracking-tighter">
                ›››
              </span>
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/teams" data-testid="landing-cta-teams">
              matches &amp; livestreams
            </Link>
          </Button>
        </div>
      </div>
      <div
        className="flex flex-1 items-center justify-center"
        aria-hidden="true"
      >
        <HeroViewfinder />
      </div>
    </section>
  );
}

/** Hero viewfinder: full-bleed match photo framed by the brand bracket
 * marks. Falls back to the wordmark Mark if the photo asset is missing
 * (e.g. before the file lands in `web/public/hero-match.jpg`). */
function HeroViewfinder() {
  const [imageOk, setImageOk] = useState(true);
  return (
    <div className="aspect-[16/10] w-full max-w-[480px] rounded border border-rule bg-surface p-4 shadow-sm">
      <div className="relative h-full overflow-hidden rounded">
        {imageOk ? (
          <img
            src="/hero-match.jpg"
            alt=""
            className="absolute inset-0 size-full object-cover"
            onError={() => setImageOk(false)}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-surface">
            <Mark size={120} aria-hidden="true" />
          </div>
        )}
        {/* Telestration: two players circled, dashed pass line between
         * them, solid curved arrow showing the off-ball run. Mirrors
         * a real coach's freehand markup over the moment. */}
        {imageOk && <Telestration />}
        {/* Brackets render on top of the photo via DOM order. White +
         * drop-shadow keeps them visible against any frame content. */}
        <ViewfinderFrame
          label="PLAY 04 · 23:14"
          color="#ffffff"
          style={{
            position: "absolute",
            inset: 0,
            filter: "drop-shadow(0 0 2px rgba(0,0,0,0.55))",
          }}
        />
      </div>
    </div>
  );
}

/** Coach telestration overlay drawn on top of the hero photo.
 *
 * Two players circled in cyan, a dashed cyan pass line between them,
 * and a solid white curved arrow tracing the receiver's off-ball run.
 * Coords are tuned to the specific `/hero-match.jpg` framing in a
 * 160×100 viewBox (matches the 16:10 viewfinder frame). */
function Telestration() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 160 100"
      preserveAspectRatio="none"
      className="pointer-events-none absolute inset-0 size-full"
      style={{ filter: "drop-shadow(0 0 1.2px rgba(0,0,0,0.7))" }}
    >
      {/* Blue circle — ball-carrier */}
      <ellipse
        cx="38"
        cy="53"
        rx="7"
        ry="14"
        fill="none"
        stroke="#2aa3c4"
        strokeWidth="1.4"
      />

      {/* Red circle — defender */}
      <ellipse
        cx="98"
        cy="51"
        rx="7"
        ry="13"
        fill="none"
        stroke="#e84a4a"
        strokeWidth="1.4"
      />
    </svg>
  );
}

/** Render an inline span with a thin cyan rule under it — the brand's
 * accent treatment for headline emphasis that doesn't fail
 * cyan-on-white contrast. Replaces a bare `text-accent` span which
 * would read as ~1.7:1 on the light-theme canvas. */
function AccentLine({ children }: { children: React.ReactNode }) {
  return (
    <span
      data-testid="landing-hero-accent"
      className="border-b-[6px] border-accent text-ink"
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// What Tktic does — 3 feature blocks
// ---------------------------------------------------------------------------

const FEATURES: Array<{
  testid: string;
  title: string;
  body: string;
  icon: (props: { className?: string }) => JSX.Element;
}> = [
  {
    testid: "landing-feature-film",
    title: "game film, organized.",
    body:
      "Every match in one place, sorted by team and season. Upload once; coaches, players, and families all find it.",
    icon: ({ className }) => (
      <svg
        className={className}
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="5" y="7" width="22" height="18" rx="2" />
        <path d="M5 12h22M5 20h22M10 7v18M22 7v18" />
      </svg>
    ),
  },
  {
    testid: "landing-feature-feedback",
    title: "feedback that reaches the player.",
    body:
      "Mark the moment, voice the lesson, share with one player or the whole team. Notes, clips, and goals live with the video.",
    icon: ({ className }) => (
      <svg
        className={className}
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9h14a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3h-6l-6 5v-5H6a3 3 0 0 1-3-3v-6a3 3 0 0 1 3-3Z" />
        <circle cx="11" cy="15" r="1.2" fill="currentColor" />
        <circle cx="16" cy="15" r="1.2" fill="currentColor" />
        <circle cx="21" cy="15" r="1.2" fill="currentColor" />
      </svg>
    ),
  },
  {
    testid: "landing-feature-live",
    title: "live so families never miss a match.",
    body:
      "Broadcast straight from the sideline. Anyone with the team page can watch live, then come back for the replay.",
    icon: ({ className }) => (
      <svg
        className={className}
        viewBox="0 0 32 32"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="16" cy="16" r="3" fill="currentColor" />
        <path d="M9 9a10 10 0 0 0 0 14M23 9a10 10 0 0 1 0 14" />
        <path d="M5.5 5.5a14 14 0 0 0 0 21M26.5 5.5a14 14 0 0 1 0 21" />
      </svg>
    ),
  },
];

function FeatureBlocks() {
  return (
    <section
      data-testid="landing-features"
      aria-labelledby="landing-features-heading"
      className="border-t border-rule bg-elevated"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-10 px-6 py-16 lg:py-20">
        <h2
          id="landing-features-heading"
          className="font-display text-2xl tracking-display text-ink sm:text-3xl"
        >
          what tktic does.
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {FEATURES.map((f) => (
            <article
              key={f.testid}
              data-testid={f.testid}
              className="flex flex-col gap-3 rounded border border-rule bg-surface p-6"
            >
              <f.icon className="size-7 text-ink" />
              <h3 className="font-display text-lg leading-snug tracking-tight text-ink">
                {f.title}
              </h3>
              <p className="font-body text-sm leading-snug text-ink-secondary">
                {f.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Audience cards — "choose your door" moment, ranked above features
// ---------------------------------------------------------------------------

function AudienceCards() {
  return (
    <section
      data-testid="landing-audiences"
      aria-labelledby="landing-audiences-heading"
      className="border-t border-rule"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-8 px-6 py-16 lg:py-20">
        <h2
          id="landing-audiences-heading"
          className="font-display text-2xl tracking-display text-ink sm:text-3xl"
        >
          built for the people on the touchline.
        </h2>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* Coaches card — solid navy / reverse treatment per brand
           * book "reverse on Trust Navy" pattern. Outranks the feature
           * cards above through padding + type size + the reverse
           * fill. */}
          <article
            data-testid="landing-audience-coaches"
            className="flex flex-col gap-4 rounded bg-ink p-8 text-ink-inverse"
          >
            <h3 className="font-display text-2xl tracking-tight text-ink-inverse">
              coaches
            </h3>
            <p className="font-body text-base leading-snug opacity-80">
              Capture the play, mark the moment, send it to the player by
              Tuesday. Roster, notes, clips, summaries — one workspace.
            </p>
            <Button asChild variant="primary" size="sm" className="self-start">
              <Link
                to="/login"
                search={{ next: "/coach" }}
                data-testid="landing-audience-coaches-cta"
              >
                open the coach workspace
                <span aria-hidden="true" className="ml-1 tracking-tighter">
                  ›
                </span>
              </Link>
            </Button>
          </article>
          {/* Families card — paper surface, secondary CTA, matched padding. */}
          <article
            data-testid="landing-audience-families"
            className="flex flex-col gap-4 rounded border border-rule bg-elevated p-8"
          >
            <h3 className="font-display text-2xl tracking-tight text-ink">
              players &amp; families
            </h3>
            <p className="font-body text-base leading-snug text-ink-secondary">
              Watch every match, replay the moments your coach flagged, follow
              your team live. No app — just a link.
            </p>
            <Button asChild variant="outline" size="sm" className="self-start">
              <Link to="/teams" data-testid="landing-audience-families-cta">
                browse team matches
                <span aria-hidden="true" className="ml-1 tracking-tighter">
                  ›
                </span>
              </Link>
            </Button>
          </article>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function FooterStrip() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="landing-footer"
      className="border-t border-rule bg-elevated"
    >
      <div className="mx-auto flex max-w-6xl flex-col items-start gap-2 px-6 py-8 font-mono text-xs uppercase tracking-mono text-ink-muted sm:flex-row sm:items-center sm:justify-between">
        <span data-testid="landing-footer-brand">tktic · {year}</span>
        <span>built for the touchline</span>
      </div>
    </footer>
  );
}
